// Usulan dokumen (docs/contract.md section 10): an assistant message of kind
// "document_proposal" renders as one card in the thread (design review ui.region_gate
// card 0.68, relevance 2.97; tier 0, a form). Nothing is created until the
// user presses Buat dokumen.
//   head        icon tile, "Usulan dokumen", one lead
//   facts       label and value rows: jenis dokumen, sub kategori, the filled fields
//   form        the missing required fields (catalog types, validated), an
//               optional title and instruksi tambahan, then the action row:
//               Buat dokumen (the one action; Buat Dokumen left the menu, so
//               documents are made from the chat only)
//   creating    the generation progress from ui/generation.tsx
//   done        the same card becomes the confirmation with Buka dokumen
//               (design review ui.component_recipe core.card_confirmation, 0.94)
// A message of kind "document_created" renders the same confirmation.
import { useId, useMemo, useState, type FormEvent } from "react";
import type { CatalogField, CatalogTahap } from "../api";
import { ApiError } from "../api";
import { docTypeLabel, humanize, subLabel } from "../lib/catalog";
import { formatDate } from "../lib/format";
import { Markdown } from "../lib/markdown";
import { DynamicField, validateFields, type DocTypeWithOutline } from "../screens/Generate";
import { Field, fieldAria, Notice } from "../ui/components";
import { GenerationError, GenerationProgress, useElapsed } from "../ui/generation";
import { Glyph } from "../ui/icons";
import { createDocumentFromProposal, type CreateDocumentResult, type DocumentProposal, type ThreadMessage } from "./documents";
import { TurnState } from "./TurnState";

export interface CatalogState {
  data: CatalogTahap[] | null;
  error: unknown;
  reload: () => void;
}

/** The catalog definition of a proposal's document type, when the role may make it. */
export function proposalDocType(catalog: CatalogTahap[] | null, p: DocumentProposal): DocTypeWithOutline | null {
  const tahap = catalog?.find((t) => t.id === p.tahap);
  const docType = tahap?.documentTypes.find((d) => d.id === p.docType);
  if (!docType) return null;
  if (p.subCategory !== null && !docType.subCategories.some((s) => s.id === p.subCategory)) return null;
  return docType as DocTypeWithOutline;
}

/** The sub kategori picker when the proposal has none: a required select over the catalog's choices. */
export function subField(docType: DocTypeWithOutline): CatalogField {
  return {
    key: "subCategory",
    label: "Sub kategori",
    type: "select",
    required: true,
    options: docType.subCategories.map((s) => ({ value: s.id, label: s.label })),
  } as CatalogField;
}

/**
 * The fields the card asks for: required ones the proposal left empty or
 * listed as missing, and any filled value the catalog would reject.
 */
export function fieldsToAsk(fields: CatalogField[], p: DocumentProposal): CatalogField[] {
  const missing = new Set(p.missing ?? []);
  const invalid = validateFields(fields, p.input);
  return fields.filter((f) => invalid[f.key] !== undefined || (f.required && (missing.has(f.key) || !(p.input[f.key] ?? "").trim())));
}

function shownValue(f: CatalogField, v: string): string {
  if (f.type === "select") return f.options?.find((o) => o.value === v)?.label ?? v;
  // Amounts read with separators; a year or a small count stays as typed.
  if (f.type === "number" && /^\d{5,}$/.test(v)) return Number(v).toLocaleString("id-ID");
  if (f.type === "date" && /^\d{4}-\d{2}-\d{2}$/.test(v)) return formatDate(`${v}T00:00:00`);
  return v;
}

type Phase =
  | { kind: "form" }
  | { kind: "creating" }
  | { kind: "failed"; error: ApiError }
  | { kind: "blocked"; error: ApiError }
  | { kind: "done"; id: string; title: string };

export interface ProposalCardProps {
  message: ThreadMessage;
  conversationId: string;
  catalog: CatalogState;
  onCreated: (messageId: string, result: CreateDocumentResult) => void;
}

export function ProposalCard({ message, conversationId, catalog, onCreated }: ProposalCardProps) {
  const p = message.proposal!;
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const prefix = `p${uid}-`;
  const headId = `${prefix}head`;
  const docType = useMemo(() => proposalDocType(catalog.data, p), [catalog.data, p]);
  const ask = useMemo(() => (docType ? fieldsToAsk(docType.fields, p) : []), [docType, p]);
  const askKeys = new Set(ask.map((f) => f.key));
  const pickSub = p.subCategory === null && docType !== null;
  const subAsk = pickSub ? subField(docType!) : null;
  const [values, setValues] = useState<Record<string, string>>(() => ({}));
  const [chosenSub, setChosenSub] = useState("");
  const [title, setTitle] = useState(p.title ?? "");
  const [instructions, setInstructions] = useState(p.instructions ?? "");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [phase, setPhase] = useState<Phase>({ kind: "form" });
  const seconds = useElapsed(phase.kind === "creating");

  const jenis = docTypeLabel(catalog.data, p.docType);
  const subId = p.subCategory ?? (chosenSub || null);
  const sub = subId ? subLabel(catalog.data, p.docType, subId) : null;
  const doneId = phase.kind === "done" ? phase.id : (message.documentId ?? null);

  if (doneId) {
    const name = phase.kind === "done" ? phase.title : (p.title?.trim() || jenis);
    return <CreatedCard documentId={doneId} title={name} detail={sub ? `${jenis}, ${sub}` : jenis} />;
  }

  // The final input: the proposal's values that still hold, plus what the user typed.
  const final = (): Record<string, string> => {
    const out: Record<string, string> = {};
    for (const f of docType?.fields ?? []) {
      const v = (askKeys.has(f.key) ? values[f.key] : p.input[f.key]) ?? "";
      if (v.trim()) out[f.key] = v.trim();
    }
    return out;
  };

  const create = async () => {
    if (!docType) return;
    setPhase({ kind: "creating" });
    try {
      const result = await createDocumentFromProposal(conversationId, message.id, {
        ...(p.subCategory === null && chosenSub ? { subCategory: chosenSub } : {}),
        input: final(),
        title: title.trim() || undefined,
        instructions: instructions.trim() || undefined,
      });
      setPhase({ kind: "done", id: result.document.id, title: result.document.title });
      onCreated(message.id, result);
    } catch (err) {
      const error = err instanceof ApiError ? err : new ApiError(0, "Tidak dapat terhubung ke server. Periksa koneksi lalu coba lagi.");
      if (error.status === 400 || error.status === 422) {
        setErrors({ _form: error.message });
        setPhase({ kind: "form" });
      } else if (error.status === 403 || error.status === 409) {
        setPhase({ kind: "blocked", error });
      } else {
        setPhase({ kind: "failed", error });
      }
    }
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!docType) return;
    const asked = subAsk ? [subAsk, ...ask] : ask;
    const found = validateFields(asked, { ...values, subCategory: chosenSub });
    setErrors(found);
    const first = asked.find((f) => found[f.key]);
    if (first) {
      document.getElementById(`${prefix}${first.key}`)?.focus();
      return;
    }
    void create();
  };

  const head = (
    <div className="proposal-head">
      <span className="proposal-icon" aria-hidden="true">
        <Glyph name="docAdd" />
      </span>
      <div className="proposal-head-text">
        <h2 id={headId} className="proposal-title">
          Usulan dokumen
        </h2>
        <p className="proposal-lead">Periksa isian dari percakapan. Dokumen baru dibuat setelah Anda menekan Buat dokumen.</p>
      </div>
    </div>
  );

  const filled = (docType?.fields ?? []).filter((f) => !askKeys.has(f.key) && (p.input[f.key] ?? "").trim());
  const facts = (
    <dl className="proposal-facts">
      <div className="proposal-fact">
        <dt>Jenis dokumen</dt>
        <dd>{jenis}</dd>
      </div>
      {p.subCategory ? (
        <div className="proposal-fact">
          <dt>Sub kategori</dt>
          <dd>{sub}</dd>
        </div>
      ) : null}
      {catalog.data
        ? filled.map((f) => (
            <div key={f.key} className="proposal-fact">
              <dt>{f.label}</dt>
              <dd>{shownValue(f, p.input[f.key]!)}</dd>
            </div>
          ))
        : Object.entries(p.input)
            .filter(([, v]) => v.trim())
            .map(([k, v]) => (
              <div key={k} className="proposal-fact">
                <dt>{humanize(k)}</dt>
                <dd>{v}</dd>
              </div>
            ))}
    </dl>
  );

  let body;
  if (phase.kind === "creating") {
    body = <GenerationProgress what={jenis} seconds={seconds} outline={docType?.outline} />;
  } else if (phase.kind === "failed") {
    body = (
      <GenerationError
        error={phase.error}
        onRetry={() => void create()}
        secondary={
          <button type="button" className="btn btn-secondary" onClick={() => setPhase({ kind: "form" })}>
            Ubah isian
          </button>
        }
      />
    );
  } else if (phase.kind === "blocked") {
    const used = phase.error.status === 409;
    body = (
      <TurnState
        tone={used ? "warning" : "danger"}
        title={used ? "Usulan ini sudah dipakai" : "Dokumen tidak dapat dibuat"}
        lines={[phase.error.message, used ? "Satu usulan hanya dapat dipakai sekali. Dokumennya ada di daftar dokumen Anda." : "Peran Anda tidak dapat membuat jenis dokumen ini."]}
        action={
          used ? (
            <a className="btn btn-secondary" href="/dokumen">
              <Glyph name="documentText" />
              <span>Lihat daftar dokumen</span>
            </a>
          ) : undefined
        }
      />
    );
  } else if (catalog.error && !catalog.data) {
    body = (
      <TurnState
        tone="danger"
        title="Isian dokumen belum termuat"
        lines={["Katalog dokumen belum dapat dimuat, jadi isian yang kurang belum dapat ditampilkan."]}
        action={
          <button type="button" className="btn btn-secondary" onClick={catalog.reload}>
            <Glyph name="refresh" />
            <span>Coba lagi</span>
          </button>
        }
      />
    );
  } else if (catalog.data && !docType) {
    body = (
      <TurnState
        tone="warning"
        title="Jenis dokumen ini tidak tersedia untuk Anda"
        lines={["Usulan ini memakai jenis dokumen yang tidak dapat Anda buat. Minta asisten menyusun jenis dokumen lain di percakapan ini."]}
      />
    );
  } else {
    const loadingFields = !catalog.data;
    body = (
      <form className="form-stack proposal-form" noValidate onSubmit={submit} aria-labelledby={headId}>
        {errors._form ? (
          <Notice tone="danger" title="Isian belum diterima">
            <p>{errors._form}</p>
          </Notice>
        ) : null}
        {ask.length > 0 || subAsk ? (
          <div className="proposal-ask">
            <h3 className="card-subtitle">Lengkapi isian</h3>
            {subAsk ? <DynamicField idPrefix={prefix} field={subAsk} value={chosenSub} error={errors.subCategory} onChange={setChosenSub} /> : null}
            {ask.map((f) => (
              <DynamicField key={f.key} idPrefix={prefix} field={f} value={values[f.key] ?? ""} error={errors[f.key]} onChange={(v) => setValues((s) => ({ ...s, [f.key]: v }))} />
            ))}
          </div>
        ) : null}
        <Field id={`${prefix}title`} label="Judul dokumen" hint="Opsional. Kosongkan untuk memakai judul otomatis.">
          <input {...fieldAria(`${prefix}title`)} value={title} placeholder={jenis} maxLength={200} onChange={(e) => setTitle(e.currentTarget.value)} />
        </Field>
        <Field id={`${prefix}instructions`} label="Instruksi tambahan untuk Pluto" hint="Opsional. Contoh: gunakan bahasa formal dan sertakan tabel rincian biaya.">
          <textarea {...fieldAria(`${prefix}instructions`)} rows={3} value={instructions} maxLength={2000} onChange={(e) => setInstructions(e.currentTarget.value)} />
        </Field>
        <div className="button-row">
          <button type="submit" className="btn" disabled={loadingFields} aria-busy={loadingFields || undefined}>
            <Glyph name="docAdd" />
            <span>Buat dokumen</span>
          </button>
        </div>
        <p className="form-note">{loadingFields ? "Memuat isian dari katalog dokumen." : "Memakai kuota pesan. Hasilnya draf yang wajib Anda periksa."}</p>
      </form>
    );
  }

  return (
    <section className="card proposal" aria-labelledby={headId} data-state={phase.kind}>
      {head}
      {facts}
      {body}
    </section>
  );
}

/** The confirmation: the document now exists as a Draft. */
export function CreatedCard({ documentId, title, detail, content }: { documentId: string; title?: string | null; detail?: string | null; content?: string | null }) {
  const headId = `created-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  return (
    <section className="card proposal" aria-labelledby={headId} data-state="done">
      <div className="proposal-head">
        <span className="proposal-icon" data-tone="success" aria-hidden="true">
          <Glyph name="tickCircle" />
        </span>
        <div className="proposal-head-text">
          <h2 id={headId} className="proposal-title">
            Dokumen dibuat
          </h2>
          {title ? <p className="proposal-name">{title}</p> : null}
          <p className="proposal-lead">{detail ? `${detail}. ` : ""}Tersimpan sebagai Draft untuk Anda periksa sebelum diajukan.</p>
        </div>
      </div>
      {content ? <Markdown source={content} className="msg-md proposal-md" headingOffset={1} /> : null}
      <div className="button-row">
        <a className="btn" href={`/dokumen/${encodeURIComponent(documentId)}`}>
          <Glyph name="documentText" />
          <span>Buka dokumen</span>
        </a>
      </div>
    </section>
  );
}
